/*
 * Keeps first-party grouped add-on metadata aligned with the paid child lines.
 * Grouped mutations are serialized because updating parent properties rotates
 * Shopify's line key. Legacy/property-only carts never enter this path.
 */
(function () {
  'use strict';

  var GROUP_PROPERTY = '_wf_addon_group';
  var CHILD_PROPERTY = '_wf_addon_child';
  var NATIVE_PROPERTY = '_wf_addons';
  var DISPLAY_PROPERTY = 'Add-Ons';
  var DEFAULT_DISPLAY = 'No, Thank you';
  var queue = Promise.resolve();

  function cartUrl(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    return root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  function propertiesOf(line) {
    return line && line.properties && typeof line.properties === 'object' ? line.properties : {};
  }

  function groupOf(line) {
    return String(propertiesOf(line)[GROUP_PROPERTY] || '').trim();
  }

  function isChild(line) {
    return String(propertiesOf(line)[CHILD_PROPERTY] || '').trim().toLowerCase() === 'true';
  }

  function findParent(cart, group) {
    return (cart.items || []).find(function (line) {
      return groupOf(line) === group && !isChild(line);
    }) || null;
  }

  function childrenFor(cart, group) {
    return (cart.items || []).filter(function (line) {
      return groupOf(line) === group && isChild(line);
    });
  }

  function addonsFromChildren(children) {
    return children.map(function (line) {
      var quantity = Number(line.quantity || 1);
      return {
        t: String(line.product_title || line.title || line.name || '').trim(),
        q: Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1
      };
    }).filter(function (addon) {
      return addon.t;
    });
  }

  function summaryFor(addons) {
    if (!addons.length) return DEFAULT_DISPLAY;
    return addons.map(function (addon) {
      return addon.q > 1 ? addon.t + ' x' + addon.q : addon.t;
    }).join(', ');
  }

  function request(path, body) {
    return fetch(cartUrl(path), body ? {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    } : undefined).then(function (response) {
      return response.json().then(function (state) {
        if (!response.ok || state.status === 422 || state.errors) {
          var error = new Error(state.message || state.description || state.errors || 'Cart update failed');
          error.status = state.status || response.status;
          error.wfAddonNonRetryable = true;
          throw error;
        }
        return state;
      });
    });
  }

  function fetchCart() {
    return request('cart.js');
  }

  function sectionFields(options) {
    var fields = {};
    if (options && Array.isArray(options.sections) && options.sections.length) fields.sections = options.sections;
    if (options && options.sectionsUrl) fields.sections_url = options.sectionsUrl;
    return fields;
  }

  function withSections(body, options) {
    return Object.assign(body, sectionFields(options));
  }

  function verifyProtectedProperties(before, after) {
    Object.keys(before).forEach(function (name) {
      if (name === DISPLAY_PROPERTY || name === NATIVE_PROPERTY) return;
      if (String(after[name] ?? '') !== String(before[name] ?? '')) {
        var error = new Error('Cart add-on reconciliation did not preserve property: ' + name);
        error.wfAddonNonRetryable = true;
        throw error;
      }
    });
  }

  function parseNative(value) {
    if (!value) return [];
    try {
      var parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(function (addon) {
        return { t: String(addon && addon.t || '').trim(), q: Number(addon && addon.q || 1) };
      }).filter(function (addon) { return addon.t; });
    } catch (_) {
      return [];
    }
  }

  function sameAddons(left, right) {
    if (left.length !== right.length) return false;
    return left.every(function (addon, index) {
      return addon.t === right[index].t && addon.q === right[index].q;
    });
  }

  function parentNeedsSync(parent, addons) {
    var props = propertiesOf(parent);
    if (String(props[DISPLAY_PROPERTY] || '') !== summaryFor(addons)) return true;
    if (!addons.length) return Boolean(props[NATIVE_PROPERTY]);
    return !sameAddons(parseNative(props[NATIVE_PROPERTY]), addons);
  }

  function patchParent(cart, group, options) {
    var parent = findParent(cart, group);
    if (!parent) return Promise.resolve({ cart: cart, response: cart });

    var addons = addonsFromChildren(childrenFor(cart, group));
    var originalProperties = Object.assign({}, propertiesOf(parent));
    var properties = Object.assign({}, originalProperties, {
      [DISPLAY_PROPERTY]: summaryFor(addons),
      [GROUP_PROPERTY]: group
    });
    if (addons.length) properties[NATIVE_PROPERTY] = JSON.stringify(addons);
    else delete properties[NATIVE_PROPERTY];

    if (!parentNeedsSync(parent, addons)) return Promise.resolve({ cart: cart, response: cart });

    return request('cart/change.js', withSections({
      id: parent.key,
      quantity: parent.quantity,
      properties: properties
    }, options)).then(function (response) {
      return fetchCart().then(function (verifiedCart) {
        var verifiedParent = findParent(verifiedCart, group);
        if (!verifiedParent) {
          var missingParentError = new Error('Cart add-on parent disappeared during reconciliation');
          missingParentError.wfAddonNonRetryable = true;
          throw missingParentError;
        }
        var verifiedProperties = propertiesOf(verifiedParent);
        verifyProtectedProperties(originalProperties, verifiedProperties);
        if (String(verifiedProperties[DISPLAY_PROPERTY] || '') !== summaryFor(addons)) {
          var summaryError = new Error('Cart add-on summary verification failed');
          summaryError.wfAddonNonRetryable = true;
          throw summaryError;
        }
        if (addons.length) {
          if (!sameAddons(parseNative(verifiedProperties[NATIVE_PROPERTY]), addons)) {
            var metadataError = new Error('Cart add-on metadata verification failed');
            metadataError.wfAddonNonRetryable = true;
            throw metadataError;
          }
        } else if (verifiedProperties[NATIVE_PROPERTY]) {
          var removalError = new Error('Removed cart add-ons remained in metadata');
          removalError.wfAddonNonRetryable = true;
          throw removalError;
        }
        return {
          cart: verifiedCart,
          response: Object.assign({}, verifiedCart, { sections: response.sections })
        };
      });
    });
  }

  function mutateGroupedLine(itemKey, quantity, meta, options) {
    return fetchCart().then(function (cart) {
      var target = (cart.items || []).find(function (line) { return line.key === itemKey; });
      if (!target && meta && meta.isChild === false) target = findParent(cart, meta.group);
      if (!target) throw new Error('Cart line changed before the add-on update could run');

      var group = groupOf(target) || meta.group;
      if (!group) throw new Error('Grouped cart line is missing its add-on group');

      if (!isChild(target) && Number(quantity) === 0) {
        var updates = {};
        (cart.items || []).forEach(function (line) {
          if (groupOf(line) === group) updates[line.key] = 0;
        });
        return request('cart/update.js', withSections({ updates: updates }, options)).then(function (response) {
          return fetchCart().then(function (verifiedCart) {
            if ((verifiedCart.items || []).some(function (line) { return groupOf(line) === group; })) {
              var cascadeError = new Error('Grouped add-on lines remained after parent removal');
              cascadeError.wfAddonNonRetryable = true;
              throw cascadeError;
            }
            return Object.assign({}, verifiedCart, { sections: response.sections });
          });
        });
      }

      return request('cart/change.js', withSections({ id: target.key, quantity: Number(quantity) }, options))
        .then(function (response) {
          return fetchCart().then(function (updatedCart) {
            if (!isChild(target) || !findParent(updatedCart, group)) {
              return Object.assign({}, updatedCart, { sections: response.sections });
            }
            var updatedParent = findParent(updatedCart, group);
            if (!parentNeedsSync(updatedParent, addonsFromChildren(childrenFor(updatedCart, group)))) {
              return Object.assign({}, updatedCart, { sections: response.sections });
            }
            return patchParent(updatedCart, group, options).then(function (result) {
              return result.response;
            });
          });
        });
    });
  }

  function enqueue(task) {
    var operation = queue.then(task, task);
    queue = operation.catch(function () {});
    return operation;
  }

  function reconcileOnce(options) {
    return fetchCart().then(function reconcileCart(cart) {
      var groups = [];
      (cart.items || []).forEach(function (line) {
        var group = groupOf(line);
        if (group && !isChild(line) && groups.indexOf(group) === -1) groups.push(group);
      });

      var latest = Promise.resolve({ cart: cart, response: cart });
      groups.forEach(function (group) {
        latest = latest.then(function (result) {
          return patchParent(result.cart, group, options);
        });
      });
      return latest.then(function (result) { return result.response; });
    });
  }

  function describe(itemKey) {
    var marker = document.getElementById('remove-' + itemKey) || document.getElementById('cart-qty-' + itemKey);
    var row = marker && marker.closest ? marker.closest('[data-wf-addon-group]') : null;
    if (!row && typeof document.querySelectorAll === 'function') {
      var groupedRows = document.querySelectorAll('[data-wf-addon-key]');
      for (var i = 0; i < groupedRows.length; i += 1) {
        if (groupedRows[i].dataset.wfAddonKey === itemKey) {
          row = groupedRows[i];
          break;
        }
      }
    }
    if (!row || !row.dataset.wfAddonGroup) return null;
    return {
      group: row.dataset.wfAddonGroup,
      isChild: row.dataset.wfAddonChild === 'true'
    };
  }

  window.WF_ADDON_CART_SYNC = {
    describe: describe,
    mutateLine: function (itemKey, quantity, meta, options) {
      return enqueue(function () { return mutateGroupedLine(itemKey, quantity, meta, options || {}); });
    },
    reconcileCart: function (options) {
      options = options || {};
      var retries = Number(options.retries || 0);
      return enqueue(function run() {
        return reconcileOnce(options).catch(function (error) {
          if (retries <= 0 || error.wfAddonNonRetryable) throw error;
          retries -= 1;
          return run();
        });
      });
    }
  };
})();
